import { InfoCircleOutlined } from '@ant-design/icons';
import { Modal, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../api/AuthContext.js';
import ImageUploadCropper from '../components/ImageUploadCropper.js';
import { createGathering } from '../api/createGathering.js';
import { deleteGathering } from '../api/deleteGathering.js';
import { fetchGatheringsByOwner, fetchGatheringStats } from '../api/fetchGatherings.js';
import { updateGathering } from '../api/updateGathering.js';
import { showToast, updateToast } from '../utils/toastUtils.js';

function Groups() {
    const auth = useAuth();
    const navigate = useNavigate();

    const [gatherings, setGatherings] = useState(null);
    const [filteredGatherings, setFilteredGatherings] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [gatheringStats, setGatheringStats] = useState({});
    const [loadingStats, setLoadingStats] = useState(false);

    // Modal states
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
    const [currentGathering, setCurrentGathering] = useState(null);
    const [gatheringName, setGatheringName] = useState('');
    const [gatheringImage, setGatheringImage] = useState('');

    // Fetch all gatherings
    const getAllGatherings = async () => {
        try {
            const data = await fetchGatheringsByOwner(auth.currentUser.user_id);
            setGatherings(data);
            setFilteredGatherings(data);
            
            // Fetch stats for each gathering
            if (data && data.length > 0) {
                fetchAllStats(data);
            }
        } catch (err) {
            console.error('Error fetching gatherings:', err);
            showToast('Failed to load groups', 'error');
        }
    };

    // Fetch statistics for all gatherings
    const fetchAllStats = async (gatheringsList) => {
        setLoadingStats(true);
        const statsMap = {};
        
        for (const gathering of gatheringsList) {
            try {
                const stats = await fetchGatheringStats(gathering.gathering_id);
                statsMap[gathering.gathering_id] = stats;
            } catch (err) {
                console.error(`Error fetching stats for ${gathering.gathering_id}:`, err);
            }
        }
        
        setGatheringStats(statsMap);
        setLoadingStats(false);
    };

    useEffect(() => {
        getAllGatherings();
    }, []);

    // Search filter
    useEffect(() => {
        if (!gatherings) return;
        
        if (searchText.trim() === '') {
            setFilteredGatherings(gatherings);
        } else {
            const filtered = gatherings.filter((g) =>
                g.name.toLowerCase().includes(searchText.toLowerCase())
            );
            setFilteredGatherings(filtered);
        }
    }, [searchText, gatherings]);

    // Modal handlers
    const showCreateModal = () => {
        setModalMode('create');
        setGatheringName('');
        setGatheringImage('');
        setCurrentGathering(null);
        setIsModalVisible(true);
    };

    const showEditModal = (gathering) => {
        setModalMode('edit');
        setGatheringName(gathering.name);
        setGatheringImage(gathering.image_url || '');
        setCurrentGathering(gathering);
        setIsModalVisible(true);
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setGatheringName('');
        setGatheringImage('');
        setCurrentGathering(null);
    };

    const handleModalOk = async () => {
        if (!gatheringName.trim()) {
            const toastId = showToast('Please enter a group name', auth.currentUser.email, {
                toastId: 'toastIdGroupValidation',
            });
            updateToast(toastId, auth.currentUser.email, {
                render: 'Please enter a group name',
                type: 'error',
                isLoading: false,
                autoClose: 2000,
            });
            return;
        }

        const toastId = showToast(
            modalMode === 'create' ? 'Creating group...' : 'Updating group...',
            auth.currentUser.email,
            { toastId: modalMode === 'create' ? 'toastIdCreateGroup' : 'toastIdUpdateGroup' }
        );

        try {
            if (modalMode === 'create') {
                await createGathering({
                    owner_id: auth.currentUser.user_id,
                    name: gatheringName,
                    image_url: gatheringImage || null,
                });
                updateToast(toastId, auth.currentUser.email, {
                    render: 'Group created successfully',
                    type: 'success',
                    isLoading: false,
                    autoClose: 1500,
                });
            } else {
                await updateGathering(
                    currentGathering.gathering_id,
                    auth.currentUser.user_id,
                    gatheringName,
                    gatheringImage || null
                );
                updateToast(toastId, auth.currentUser.email, {
                    render: 'Group updated successfully',
                    type: 'success',
                    isLoading: false,
                    autoClose: 1500,
                });
            }

            handleModalCancel();
            getAllGatherings();
        } catch (err) {
            console.error('Error saving gathering:', err);
            updateToast(toastId, auth.currentUser.email, {
                render: `Failed to ${modalMode} group`,
                type: 'error',
                isLoading: false,
                autoClose: 2000,
            });
        }
    };

    const handleDelete = async (gathering) => {
        Modal.confirm({
            title: 'Delete Group',
            content: `Are you sure you want to delete "${gathering.name}"? This will not delete the events in this group.`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                const toastId = showToast('Deleting group...', auth.currentUser.email, {
                    toastId: 'toastIdDeleteGroup',
                });

                try {
                    await deleteGathering(gathering.gathering_id);
                    updateToast(toastId, auth.currentUser.email, {
                        render: 'Group deleted successfully',
                        type: 'success',
                        isLoading: false,
                        autoClose: 1500,
                    });
                    getAllGatherings();
                } catch (err) {
                    console.error('Error deleting gathering:', err);
                    updateToast(toastId, auth.currentUser.email, {
                        render: 'Failed to delete group',
                        type: 'error',
                        isLoading: false,
                        autoClose: 2000,
                    });
                }
            },
        });
    };

    const handleViewDetails = (gathering) => {
        navigate(`/admin/groups/${gathering.gathering_id}`);
    };

    const isLoading = gatherings === null;
    const hasGatherings = filteredGatherings && filteredGatherings.length > 0;

    const tooltipOverview = 'Groups help you organize your events into categories or teams.';

    return (
        <>
            <div style={{ marginBottom: '24px' }}>
                <h1 style={{ marginBottom: '8px' }}>
                    Groups{' '}
                    <Tooltip title={tooltipOverview}>
                        <InfoCircleOutlined />
                    </Tooltip>
                </h1>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                    Organize your events into groups for better management
                </p>
            </div>

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                    gap: '12px',
                }}
            >
                <div style={{ flex: 1, maxWidth: '400px', position: 'relative' }}>
                    <input
                        className="input"
                        type="text"
                        placeholder="Search groups by name..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{
                            fontSize: '14px',
                            paddingRight: searchText ? '35px' : '10px',
                        }}
                    />
                    {searchText && (
                        <button
                            type="button"
                            onClick={() => setSearchText('')}
                            style={{
                                position: 'absolute',
                                right: '8px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#999',
                                fontSize: '18px',
                                lineHeight: '1',
                                transition: 'color 0.2s ease',
                            }}
                            onMouseEnter={(e) => { e.target.style.color = '#333'; }}
                            onMouseLeave={(e) => { e.target.style.color = '#999'; }}
                            aria-label="Clear search"
                        >
                            ×
                        </button>
                    )}
                </div>
            </div>

            {/* Loading state */}
            {isLoading && (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                    Loading...
                </div>
            )}

            {/* Empty state */}
            {!isLoading && !hasGatherings && searchText === '' && (
                <div style={{
                    textAlign: 'center',
                    padding: '80px 20px',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                    <div style={{
                        fontSize: '64px',
                        marginBottom: '24px',
                        opacity: 0.8
                    }}>📁</div>
                    <h2 style={{
                        marginBottom: '12px',
                        color: '#1f2937',
                        fontSize: '24px',
                        fontWeight: '600'
                    }}>
                        No groups yet
                    </h2>
                    <p style={{
                        fontSize: '15px',
                        color: '#6b7280',
                        marginBottom: '32px',
                        maxWidth: '500px',
                        margin: '0 auto 32px',
                        lineHeight: '1.6'
                    }}>
                        Create your first group to organize your events into categories or teams. Groups help you manage multiple events together.
                    </p>
                    <button
                        type="button"
                        className="button is-primary"
                        onClick={showCreateModal}
                        style={{
                            fontSize: '15px',
                            padding: '12px 32px',
                            fontWeight: '500'
                        }}
                    >
                        📁 Create Your First Group
                    </button>
                </div>
            )}

            {/* No search results */}
            {!isLoading && !hasGatherings && searchText !== '' && (
                <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: '#6b7280',
                    background: '#f9fafb',
                    borderRadius: '8px'
                }}>
                    No groups found matching "{searchText}"
                </div>
            )}

            {/* Groups table */}
            {!isLoading && hasGatherings && (
                <>
                    {searchText && (
                        <div style={{
                            fontSize: '13px',
                            color: '#666',
                            margin: '4px 4px 12px 4px',
                            padding: '4px',
                        }}>
                            Found {filteredGatherings.length} group{filteredGatherings.length !== 1 ? 's' : ''}
                        </div>
                    )}
                    <div style={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        marginBottom: '20px',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                    }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse'
                        }}>
                            <thead>
                                <tr style={{
                                    backgroundColor: '#f9fafb',
                                    borderBottom: '2px solid #e5e7eb'
                                }}>
                                    <th style={{
                                        padding: '8px 12px',
                                        textAlign: 'left',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        color: '#6b7280',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        minWidth: '250px'
                                    }}>Group Name</th>
                                    <th style={{
                                        padding: '8px 12px',
                                        textAlign: 'center',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        color: '#6b7280',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        width: '120px'
                                    }}>Events</th>
                                    <th style={{
                                        padding: '8px 12px',
                                        textAlign: 'center',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        color: '#6b7280',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        width: '140px'
                                    }}>Participants</th>
                                    <th style={{
                                        padding: '8px 12px',
                                        textAlign: 'left',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        color: '#6b7280',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        width: '180px'
                                    }}>Status</th>
                                    <th style={{
                                        padding: '8px 12px',
                                        textAlign: 'right',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        color: '#6b7280',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        width: '200px'
                                    }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredGatherings.map((gathering) => {
                                    const stats = gatheringStats[gathering.gathering_id];

                                    return (
                                        <tr
                                            key={gathering.gathering_id}
                                            style={{
                                                borderBottom: '1px solid #e5e7eb',
                                                transition: 'background-color 0.2s',
                                                cursor: 'pointer'
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                            onClick={() => handleViewDetails(gathering)}
                                        >
                                            <td style={{ padding: '12px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                                    {gathering.image_url ? (
                                                        <img
                                                            src={gathering.image_url}
                                                            alt={gathering.name}
                                                            style={{
                                                                width: '48px',
                                                                height: '48px',
                                                                borderRadius: '6px',
                                                                objectFit: 'cover',
                                                                border: '1px solid #e5e7eb'
                                                            }}
                                                        />
                                                    ) : (
                                                        <div style={{
                                                            width: '48px',
                                                            height: '48px',
                                                            borderRadius: '6px',
                                                            background: 'linear-gradient(135deg, #f0f0f0 0%, #e5e7eb 100%)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '24px',
                                                            border: '1px solid #e5e7eb'
                                                        }}>
                                                            📁
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '15px', marginBottom: '2px' }}>
                                                            {gathering.name}
                                                        </div>
                                                        {stats && (
                                                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                                                {stats.total_events} {stats.total_events === 1 ? 'event' : 'events'} · {stats.total_participants} {stats.total_participants === 1 ? 'participant' : 'participants'}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '12px', textAlign: 'center' }}>
                                                {loadingStats ? (
                                                    <span style={{ color: '#9ca3af', fontSize: '14px' }}>...</span>
                                                ) : stats ? (
                                                    <span style={{
                                                        color: '#1f2937',
                                                        fontWeight: '600',
                                                        fontSize: '16px'
                                                    }}>
                                                        {stats.total_events}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#9ca3af' }}>-</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '12px', textAlign: 'center' }}>
                                                {loadingStats ? (
                                                    <span style={{ color: '#9ca3af', fontSize: '14px' }}>...</span>
                                                ) : stats ? (
                                                    <span style={{
                                                        color: '#1f2937',
                                                        fontWeight: '600',
                                                        fontSize: '16px'
                                                    }}>
                                                        {stats.total_participants}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#9ca3af' }}>-</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '12px' }}>
                                                {loadingStats ? (
                                                    <span style={{ color: '#9ca3af', fontSize: '14px' }}>...</span>
                                                ) : stats ? (
                                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                        {stats.active_events > 0 && (
                                                            <span style={{
                                                                fontSize: '12px',
                                                                padding: '4px 10px',
                                                                borderRadius: '12px',
                                                                background: '#d1fae5',
                                                                color: '#065f46',
                                                                fontWeight: '600'
                                                            }}>
                                                                {stats.active_events} Active
                                                            </span>
                                                        )}
                                                        {stats.paused_events > 0 && (
                                                            <span style={{
                                                                fontSize: '12px',
                                                                padding: '4px 10px',
                                                                borderRadius: '12px',
                                                                background: '#fef3c7',
                                                                color: '#92400e',
                                                                fontWeight: '600'
                                                            }}>
                                                                {stats.paused_events} Paused
                                                            </span>
                                                        )}
                                                        {stats.total_events === 0 && (
                                                            <span style={{
                                                                fontSize: '12px',
                                                                padding: '4px 10px',
                                                                borderRadius: '12px',
                                                                background: '#f3f4f6',
                                                                color: '#6b7280',
                                                                fontWeight: '500'
                                                            }}>
                                                                No events
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span style={{ color: '#9ca3af' }}>-</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '12px', verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
                                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                                    <button
                                                        type="button"
                                                        className="button is-light"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleViewDetails(gathering);
                                                        }}
                                                        style={{
                                                            fontSize: '11px',
                                                            fontWeight: '500',
                                                            padding: '4px 10px',
                                                            height: 'auto'
                                                        }}
                                                    >
                                                        View
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="button is-light"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            showEditModal(gathering);
                                                        }}
                                                        style={{
                                                            fontSize: '11px',
                                                            fontWeight: '500',
                                                            padding: '4px 10px',
                                                            height: 'auto'
                                                        }}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="button is-light"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(gathering);
                                                        }}
                                                        style={{
                                                            fontSize: '11px',
                                                            color: '#dc2626',
                                                            fontWeight: '500',
                                                            padding: '4px 10px',
                                                            height: 'auto'
                                                        }}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <button
                        type="button"
                        className="button is-primary is-small"
                        onClick={showCreateModal}
                    >
                        + Create new
                    </button>
                </>
            )}

            {/* Create/Edit Modal */}
            <Modal
                title={modalMode === 'create' ? 'Create New Group' : 'Edit Group'}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText={modalMode === 'create' ? 'Create' : 'Save'}
            >
                <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="groupName" style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                        Group Name *
                    </label>
                    <input
                        id="groupName"
                        className="input"
                        type="text"
                        placeholder="Enter group name"
                        value={gatheringName}
                        onChange={(e) => setGatheringName(e.target.value)}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                        Group Image (optional)
                    </label>
                    <ImageUploadCropper
                        value={gatheringImage}
                        onChange={setGatheringImage}
                        aspectRatio={1}
                        cropShape="rect"
                    />
                </div>
            </Modal>
        </>
    );
}

export default Groups;

